import { useEffect, useState } from 'react';
import { getAnswerLists, getRatingSpellings } from '@/services/pilots.service.js';

// A FIXED ANSWER IS A PICK FROM ITS ONE LIST (David 2026-11-04: "Every answer that has a fixed set of values is a picklist,
// never free text ... one each, everywhere"). The list is the server's (GET /api/tracon/answer-lists, utils/answer-lists);
// no surface holds a copy. A stored value that is not on the list is shown as it is, marked, and is never guessed onto the
// list or replaced by a default; a blank reads "Choose".
//   field     the list (aircraft_use, storage_type, coverage_type, entity_type, certificate_type, medical_class)
//   value     the stored value; onChange(value) on a pick
export function useAnswerList(field) {
  const [list, setList] = useState(null);
  useEffect(() => {
    let live = true;
    getAnswerLists().then((l) => { if (live) setList(l[field] || []); }).catch(() => { if (live) setList([]); });
    return () => { live = false; };
  }, [field]);
  return list;
}

// The rating code for one stored spelling ("Instrument Rated", "SEL (Single-Engine Land)", "Rotorwing"), or null when it is
// not one of the ten (undefined until the table is read); the server's table (utils/answer-lists RATING_SPELLINGS), the key
// shaped as ratingCode shapes it (tests/answer-lists-one-list.test.js pins the two).
export function useRatingCode() {
  const [table, setTable] = useState(null);
  useEffect(() => {
    let live = true;
    getRatingSpellings().then((t) => { if (live) setTable(t); }).catch(() => { if (live) setTable({}); });
    return () => { live = false; };
  }, []);
  return (v) => {
    if (!table) return undefined;
    const key = String(v == null ? '' : v).replace(/\s*\(.*?\)/g, '').trim().toUpperCase().replace(/-/g, ' ').replace(/\s+/g, ' ');
    return Object.prototype.hasOwnProperty.call(table, key) ? table[key] : null;
  };
}

export function labelFor(list, value) {
  const hit = (list || []).find((o) => o.value === value);
  return hit ? hit.label : value;
}

export default function ListSelect({ field, value, onChange, disabled = false, className = '', ariaLabel, blankLabel = 'Choose' }) {
  const list = useAnswerList(field);
  const v = value == null ? '' : String(value);
  const off = !!list && v.trim() !== '' && !list.some((o) => o.value === v);
  return (
    <select value={off ? '__off__' : v} disabled={disabled || !list} aria-label={ariaLabel} data-answer-list={field}
      onChange={(e) => { if (e.target.value !== '__off__') onChange(e.target.value); }}
      className={className || 'w-full rounded border border-border bg-bg px-3 py-2 text-sm text-text min-h-[44px]'}>
      {off ? <option value="__off__" disabled>{`${v} (not on the list: pick the right one)`}</option> : null}
      <option value="">{list ? blankLabel : 'Loading'}</option>
      {(list || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

// The options alone, for a surface that keeps its own <select> (its blank wording, its styling): the one list, plus the
// stored value when it is not on the list, shown as it is and marked, so the select never renders blank over an answer.
export function ListOptions({ field, stored }) {
  const list = useAnswerList(field);
  const v = stored == null ? '' : String(stored);
  const off = !!list && v.trim() !== '' && !list.some((o) => o.value === v);
  return (
    <>
      {off ? <option value={v} disabled>{`${v} (not on the list: pick the right one)`}</option> : null}
      {(list || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </>
  );
}
